import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UploadCloud, Trash2, Copy, Check, Loader2, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { api, fileUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const MediaLibrary = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/media');
      setItems(data.items || []);
    } catch (e) {
      toast({ title: 'Failed to load media', description: e?.response?.data?.detail || String(e) });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const onFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const form = new FormData();
        form.append('file', f);
        await api.post('/admin/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ok += 1;
      } catch (e) {
        toast({ title: `Upload failed: ${f.name}`, description: e?.response?.data?.detail || String(e) });
      }
    }
    setUploading(false);
    if (ok) toast({ title: 'Upload complete', description: `${ok} file(s) uploaded.` });
    await load();
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this media item? References in content will break.')) return;
    try {
      await api.delete(`/admin/media/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast({ title: 'Deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || String(e) });
    }
  };

  const onCopy = async (item) => {
    const url = fileUrl(item.url);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch (_) {
      // fallback: prompt
      window.prompt('Copy the URL:', url);
    }
  };

  const kb = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Media Library</h2>
          <p className="text-sm text-slate-400 mt-1">Upload images and videos. Copy the URL and paste it into any content field.</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => onFiles(Array.from(e.target.files || []))}
          />
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="bg-brand-green hover:bg-brand-green-light text-black font-medium btn-transition"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1.5" />}
            {uploading ? 'Uploading…' : 'Upload files'}
          </Button>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onFiles(Array.from(e.dataTransfer.files || []));
        }}
        className="rounded-2xl border-2 border-dashed border-white/10 bg-white/[0.02] p-8 text-center mb-8"
      >
        <UploadCloud className="h-8 w-8 mx-auto text-slate-400" />
        <p className="mt-3 text-sm text-slate-300">Drag & drop files here, or click “Upload files”.</p>
        <p className="mt-1 text-xs text-slate-500">JPG, PNG, WebP, GIF, SVG · MP4, WebM, MOV · up to 200 MB.</p>
      </div>

      {loading ? (
        <div className="text-slate-400 py-12 text-center">Loading media…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-400 py-12 text-center">No media yet. Upload your first file.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-white/8 bg-white/[0.02] overflow-hidden group">
              <div className="aspect-video bg-black/40 flex items-center justify-center overflow-hidden">
                {it.kind === 'image' ? (
                  <img src={fileUrl(it.url)} alt={it.original_filename} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <video src={fileUrl(it.url)} className="w-full h-full object-cover" preload="metadata" muted />
                )}
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400">
                  {it.kind === 'image' ? <ImageIcon className="h-3 w-3" /> : <VideoIcon className="h-3 w-3" />} {it.kind}
                </div>
                <div className="mt-1 text-sm text-white truncate" title={it.original_filename}>{it.original_filename}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{kb(it.size)}</div>
                <div className="mt-3 flex gap-1.5">
                  <Button
                    onClick={() => onCopy(it)}
                    variant="outline"
                    className="h-8 flex-1 border-white/10 bg-white/5 hover:bg-white/10 text-white text-xs btn-transition"
                  >
                    {copiedId === it.id ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy URL</>}
                  </Button>
                  <Button
                    onClick={() => onDelete(it.id)}
                    variant="outline"
                    className="h-8 border-white/10 bg-white/5 hover:bg-brand-pink/15 hover:border-brand-pink/40 text-white btn-transition"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
